/**
 * event-bindings.ts
 *
 * Normalizes the event-binding shapes the runtime natively and intentionally
 * accepts into a single flat map keyed `${elementId}:${eventType}`.
 *
 * Background (genquality census, 2026-08-07 — defect 1, 37 issue-instances
 * across fwd2-expense-approval-g3 + poc-account-inquiry, 24 + 13): the
 * agentic generator emits `events.json` under an `elements`-nested wrapper
 * 100% of the time (6/6 blocks across both bundles) —
 *   { "elements": { "btn-add": { "tap": { "flow": "addToCart" } } } }
 * — never the flat `bindings` map alone. The runtime accepts this shape
 * NATIVELY, confirmed by reading two independent runtime consumers:
 *   - `cogo-runtime-v2/src/app-runtime-core.ts` (~line 1700, `registerEventMap`
 *     applied to `evts.elements`) — code comment: "Generator form:
 *     { lifecycle, elements: {...} }. The agentic generator emits events
 *     under an `elements` wrapper; accept it natively so generated blocks
 *     run without a separate conversion." (PR #782)
 *   - `cogo-runtime-v2/src/render-pack.ts` (~line 415, `collectEventBindings`
 *     reads `events?.elements`) — the Zero-Copy render-pack builder.
 * Four validator rule implementations read `bundle.events?.bindings` only
 * and therefore go blind to every `elements`-shaped binding, reporting
 * "no event binding" on elements that ARE wired and DO work at runtime.
 *
 * Any rule that inspects "is this element bound" or "does this flow have a
 * binding pointing at it" MUST go through `normalizeEventBindings` — reading
 * `events.bindings` alone reproduces the blindness this module exists to fix.
 *
 * Shapes merged, all three confirmed against the runtime source above:
 *  - `events.bindings`  — flat map, already "elementId:eventType" keyed
 *    (the schema-canonical form, `events.cogo.schema.json`)
 *  - `events.elements`  — { elementId: { eventType: binding } } (the
 *    agentic generator's own output form, PR #782)
 *  - top-level v2 map   — { elementId: { eventType: binding } } directly
 *    under `events`, excluding the wrapper keys `bindings` / `elements` /
 *    `lifecycle` (app-runtime-core.ts's "v2 top-level form")
 *
 * Binding VALUES the runtime accepts (app-runtime-core.ts registerEventMap):
 *  - `{ flow: "name", ... }` object
 *  - a bare string, which the runtime wraps as `{ flow: binding }`.
 *    Leaving the string unwrapped made every contract reader that required
 *    an object (`checkEventFlowRefs`, orphan-flow `boundFlowNames`) skip it,
 *    so a working string-valued binding was dead to the validator.
 */

/** Runtime `typeof binding === "string"` arm — wrap to the object every contract reader already understands. */
function canonicalizeBinding(binding: unknown): unknown {
  if (typeof binding === "string") return { flow: binding }
  return binding
}

export function normalizeEventBindings(
  bundle: { events?: unknown } | Record<string, unknown> | undefined,
): Record<string, unknown> {
  const merged: Record<string, unknown> = {}
  if (!bundle) return merged
  const evts = (bundle as Record<string, unknown>).events as Record<string, unknown> | undefined
  if (!evts || typeof evts !== "object") return merged

  const bindings = evts.bindings as Record<string, unknown> | undefined
  if (bindings && typeof bindings === "object" && !Array.isArray(bindings)) {
    for (const [key, binding] of Object.entries(bindings)) {
      merged[key] = canonicalizeBinding(binding)
    }
  }

  const addEventMap = (elementId: string, eventMap: unknown): void => {
    if (!eventMap || typeof eventMap !== "object" || Array.isArray(eventMap)) return
    for (const [eventType, binding] of Object.entries(eventMap as Record<string, unknown>)) {
      const key = `${elementId}:${eventType}`
      if (!(key in merged)) merged[key] = canonicalizeBinding(binding)
    }
  }

  const elements = evts.elements as Record<string, unknown> | undefined
  if (elements && typeof elements === "object" && !Array.isArray(elements)) {
    for (const [elementId, eventMap] of Object.entries(elements)) {
      addEventMap(elementId, eventMap)
    }
  }

  // v2 top-level form: { elementId: { eventType: binding } } directly under
  // `events`, skipping the wrapper keys.
  for (const [elementId, eventMap] of Object.entries(evts)) {
    if (elementId === "bindings" || elementId === "elements" || elementId === "lifecycle") continue
    addEventMap(elementId, eventMap)
  }

  return merged
}

/** Set of element IDs (the part before ':') that have at least one binding, in any accepted shape. */
export function collectBoundElementIds(
  bundle: { events?: unknown } | Record<string, unknown> | undefined,
): Set<string> {
  const boundIds = new Set<string>()
  for (const key of Object.keys(normalizeEventBindings(bundle))) {
    const idx = key.indexOf(":")
    boundIds.add(idx >= 0 ? key.slice(0, idx) : key)
  }
  return boundIds
}
